import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Avatar,
  Typography,
  Tooltip,
  Skeleton,
  useTheme,
} from "@mui/material";
import { Goal } from "../../lib/types";
import { GOAL_ICONS } from "./GoalForm";

interface GoalTimelineProps {
  goals: Goal[];
  loading?: boolean;
}

const MIN_SPAN_DAYS = 30;
const OVERLAP_THRESHOLD_PCT = 3;

// Layout constants
const GOAL_ZONE_TOP = 8; // top padding for goal icons
const LANE_HEIGHT = 60; // vertical space per stagger lane
const AXIS_MARGIN_ABOVE = 16; // gap between lowest goal lane and axis
const AXIS_DOT_RADIUS_WEEK = 3;
const AXIS_DOT_RADIUS_MONTH = 6;
const LABEL_ZONE_HEIGHT = 20; // space for month labels below axis

const GoalTimeline: React.FC<GoalTimelineProps> = ({ goals, loading }) => {
  const theme = useTheme();
  const [glowingGoalId, setGlowingGoalId] = useState<string | null>(null);

  const activeGoals = useMemo(
    () => goals.filter((g) => g.status === "active"),
    [goals]
  );

  const timelineData = useMemo(() => {
    if (activeGoals.length === 0) return null;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Find the furthest target date
    let maxDays = MIN_SPAN_DAYS;
    activeGoals.forEach((goal) => {
      const target = new Date(goal.targetDate);
      const diffMs = target.getTime() - today.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays > maxDays) maxDays = diffDays;
    });

    // Add 10% padding
    const paddedSpan = maxDays * 1.1;

    // Compute positions for each goal
    const positioned = activeGoals.map((goal) => {
      const target = new Date(goal.targetDate);
      const diffMs = target.getTime() - today.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      const isPast = diffDays < 0;
      const leftPct = isPast ? 0 : (diffDays / paddedSpan) * 100;

      return { goal, leftPct: Math.min(leftPct, 97), isPast };
    });

    // Sort by leftPct for overlap detection
    positioned.sort((a, b) => a.leftPct - b.leftPct);

    // Assign lanes for overlap handling
    const lanes: number[] = [];
    positioned.forEach((item, idx) => {
      let lane = 0;
      for (let j = 0; j < idx; j++) {
        if (
          Math.abs(item.leftPct - positioned[j].leftPct) < OVERLAP_THRESHOLD_PCT
        ) {
          lane = Math.max(lane, lanes[j] + 1);
        }
      }
      lanes.push(lane);
    });

    // Generate week dots (every 7 days) with week number + year label
    const weekDots: { leftPct: number; label: string }[] = [];
    for (let d = 7; d < paddedSpan; d += 7) {
      const pct = (d / paddedSpan) * 100;
      if (pct > 98) break;
      const dotDate = new Date(today.getTime() + d * 24 * 60 * 60 * 1000);
      // ISO week number
      const tmp = new Date(dotDate.getTime());
      tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil(
        ((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
      );
      const yr = String(dotDate.getFullYear()).slice(-2);
      weekDots.push({ leftPct: pct, label: `Wk. ${weekNum} '${yr}` });
    }

    // Generate month tick marks (large dots + labels)
    const monthMarks: { label: string; leftPct: number }[] = [];
    const tickDate = new Date(today);
    tickDate.setDate(1);
    tickDate.setMonth(tickDate.getMonth() + 1);
    while (true) {
      const diffDays =
        (tickDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
      const pct = (diffDays / paddedSpan) * 100;
      if (pct > 95) break;
      monthMarks.push({
        label: tickDate.toLocaleDateString("en-US", {
          month: "short",
          year:
            tickDate.getFullYear() !== now.getFullYear()
              ? "2-digit"
              : undefined,
        }),
        leftPct: pct,
      });
      tickDate.setMonth(tickDate.getMonth() + 1);
    }

    console.debug("[GoalTimeline] Computed timeline data", {
      activeGoals: activeGoals.length,
      paddedSpanDays: Math.round(paddedSpan),
      weekDots: weekDots.length,
      monthMarks: monthMarks.length,
      positions: positioned.map((p) => ({
        name: p.goal.name,
        leftPct: Math.round(p.leftPct),
        isPast: p.isPast,
      })),
    });

    return { positioned, lanes, weekDots, monthMarks, paddedSpan };
  }, [activeGoals]);

  if (loading) {
    return (
      <Paper
        sx={{
          height: 160,
          mb: 3,
          p: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Paper>
    );
  }

  if (!timelineData || activeGoals.length === 0) {
    console.debug("[GoalTimeline] No active goals, hiding timeline");
    return null;
  }

  const { positioned, lanes, weekDots, monthMarks, paddedSpan } = timelineData;
  const maxLane = Math.max(...lanes, 0);
  const minWidth = Math.max(600, paddedSpan * 1.5);

  // Vertical positions
  const goalZoneHeight = GOAL_ZONE_TOP + (maxLane + 1) * LANE_HEIGHT;
  const axisY = goalZoneHeight + AXIS_MARGIN_ABOVE;
  const totalHeight = axisY + LABEL_ZONE_HEIGHT + 8;

  const handleGoalClick = (goalId: string) => {
    setGlowingGoalId(goalId);
    console.debug("[GoalTimeline] Goal clicked, starting glow animation", {
      goalId,
    });
  };

  return (
    <Paper
      sx={{
        mb: 3,
        p: 2,
        overflowX: "auto",
        "@keyframes goalGlow": {
          "0%": { boxShadow: "0 0 4px 2px var(--glow-color)" },
          "50%": { boxShadow: "0 0 16px 6px var(--glow-color)" },
          "100%": { boxShadow: "0 0 4px 2px var(--glow-color)" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          minWidth,
          height: totalHeight,
        }}
      >
        {/* Axis line */}
        <Box
          sx={{
            position: "absolute",
            top: axisY,
            left: 0,
            right: 0,
            height: 2,
            bgcolor: theme.palette.divider,
          }}
        />

        {/* Today marker on axis */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: axisY - 1,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
              fontSize: "0.6rem",
              mt: 0.25,
              whiteSpace: "nowrap",
            }}
          >
            Today
          </Typography>
        </Box>

        {/* Week dots on the axis */}
        {weekDots.map((dot, i) => (
          <Tooltip key={`week-${i}`} title={dot.label} arrow placement="bottom">
            <Box
              sx={{
                position: "absolute",
                left: `${dot.leftPct}%`,
                top: axisY - AXIS_DOT_RADIUS_WEEK + 1,
                width: AXIS_DOT_RADIUS_WEEK * 2,
                height: AXIS_DOT_RADIUS_WEEK * 2,
                borderRadius: "50%",
                bgcolor: theme.palette.text.disabled,
                transform: "translateX(-50%)",
                zIndex: 1,
              }}
            />
          </Tooltip>
        ))}

        {/* Month dots (large) + labels below axis */}
        {monthMarks.map((mark) => (
          <Box
            key={mark.label}
            sx={{
              position: "absolute",
              left: `${mark.leftPct}%`,
              top: axisY - AXIS_DOT_RADIUS_MONTH + 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: "translateX(-50%)",
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                width: AXIS_DOT_RADIUS_MONTH * 2,
                height: AXIS_DOT_RADIUS_MONTH * 2,
                borderRadius: "50%",
                bgcolor: theme.palette.text.secondary,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.6rem",
                mt: 0.25,
                whiteSpace: "nowrap",
              }}
            >
              {mark.label}
            </Typography>
          </Box>
        ))}

        {/* Goal items + connector lines */}
        {positioned.map((item, idx) => {
          const iconDef = GOAL_ICONS[item.goal.icon] || GOAL_ICONS.other;
          const lane = lanes[idx];
          const isGlowing = glowingGoalId === item.goal.id;

          // Goal icon center-Y: top of its lane + half avatar height
          const goalTopY = GOAL_ZONE_TOP + lane * LANE_HEIGHT;
          // Bottom of goal block (avatar 40 + caption ~14 + mt gap ~2 = ~56)
          const goalBottomY = goalTopY + 40 + 14 + 2;
          // Connector line goes from bottom of goal block to the axis
          const connectorHeight = axisY - goalBottomY;

          return (
            <React.Fragment key={item.goal.id}>
              {/* Connector line from goal to axis */}
              {connectorHeight > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${item.leftPct}%`,
                    top: goalBottomY,
                    width: 1.5,
                    height: connectorHeight,
                    bgcolor: iconDef.color + "66",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Goal icon + label */}
              <Box
                sx={{
                  position: "absolute",
                  left: `${item.leftPct}%`,
                  top: goalTopY,
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  opacity: item.isPast ? 0.5 : 1,
                  transition: "opacity 0.2s",
                  zIndex: 4,
                }}
                onClick={() => handleGoalClick(item.goal.id)}
              >
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.goal.name}
                      </Typography>
                      <Typography variant="caption">
                        Target: $
                        {item.goal.targetAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      {item.goal.description && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 0.5, opacity: 0.85 }}
                        >
                          {item.goal.description}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, opacity: 0.7 }}
                      >
                        {new Date(item.goal.targetDate).toLocaleDateString()}
                        {item.isPast && " (past due)"}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: iconDef.color,
                      "--glow-color": iconDef.color + "99",
                      ...(isGlowing && {
                        animation: "goalGlow 0.3s ease-in-out 3",
                      }),
                    }}
                    onAnimationEnd={() => setGlowingGoalId(null)}
                  >
                    {React.cloneElement(
                      iconDef.icon as React.ReactElement<{
                        style?: React.CSSProperties;
                      }>,
                      { style: { fontSize: 20 } }
                    )}
                  </Avatar>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: 80,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    fontSize: "0.6rem",
                    mt: 0.25,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {item.goal.name}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default GoalTimeline;
